import castArray from 'lodash/castArray'
import join from 'lodash/join'
import toNumber from 'lodash/toNumber'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import React, { useEffect } from 'react'

import { BackToTop } from '@Components/BackToTop'
import { SinglePostWrap } from '@Components/CMS/styled'
import { FooterWithGroupVisibility } from '@Components/Footer'
import { HeaderWithGroupVisibility } from '@Components/Header'
import { genericHeaders, PageHOC, PageProps, pageProps } from '@Components/Page'
import { ProductContext } from '@Components/Product/Context'
import { ProductLinkedData } from '@Components/Product/LinkedData'
import { REVIEW_PAGE_SIZE_ON_PDP } from '@Components/Product/Reviews'
import { Page } from '@Generated/graphql'
import { initializeApollo } from '@Globals/Apollo'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import {
  buildLinkedProductList,
  getAvailableVariantsForProduct,
  getBundledProductList,
  getCrossSellsForProduct,
  getFirstVideoIndexInAssets,
  getGalleryAssetsFromProduct,
  getPersonalizationsFromProduct,
} from '@Globals/CMS/ProductHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { gtm } from '@Globals/Google/DataLayer'
import { PreviewData } from '@Globals/Preview'
import { trackProductClick } from '@Globals/Recommendations/ProductClick'
import { OneColumn } from '@Layouts/OneColumn'
import { getSingleProduct } from '@Queries/cms/SingleProduct.query'

interface ProductProps extends PageProps {
  item: Page
}

type Params = {
  slug: string | string[]
}

export const getServerSideProps: GetServerSideProps<ProductProps, Params, PreviewData> = async (context) => {
  const {
    locale = '',
    params,
    previewData,
    req,
    res,
  } = context
  const translations = await serverSideTranslations(
    locale,
    ['cart', 'common', 'cms', 'footer', 'header', 'products', 'validation', 'shipping'],
  )
  const slug = castArray(params?.slug || [])

  if (!slug) {
    return {
      notFound: true,
    }
  }

  const apolloClient = initializeApollo(undefined, { locale, previewEnvironment: previewData?.environment })
  const post = await getSingleProduct(apolloClient, `/${join(params?.slug, '')}`, undefined, REVIEW_PAGE_SIZE_ON_PDP)

  const { page } = post.data
  if (!page) {
    return {
      notFound: true,
    }
  }

  const { id } = page
  if (!id) {
    return {
      notFound: true,
    }
  }

  const product = getProductFieldValue((page as Page)?.fields || [], 'product')
  if (!product) {
    return {
      notFound: true,
    }
  }

  genericHeaders({ req, res, surrogateKeys: [`PRO${id}`] })

  const bundledProducts = getBundledProductList(product)
  const linkedProducts = buildLinkedProductList(product)
  const { availableVariants } = getAvailableVariantsForProduct({ product })
  const unsortedAssets = getGalleryAssetsFromProduct(product)
  const firstVideoIndex = getFirstVideoIndexInAssets(unsortedAssets)
  const crossSells = getCrossSellsForProduct(product)
  const personalisations = getPersonalizationsFromProduct(product)

  return {
    props: {
      ...translations,
      ...await pageProps(
        context,
        'product',
        id,
        {
          attributes: {
            guide_info_link_type: product?.sizeGuide?.title || '',
            has_guide_info_link: !!product?.sizeGuide,
            has_translated_reviews: (product?.productReviews?.translatedCount || 0) > 0,
            has_personalisation: personalisations.length !== 0,
            has_view_more_reviews: (product?.productReviews?.productReviews?.length || 0) < (product?.productReviews?.count || 0),
            // The new design doesn't expand cross sales, so we always set this to false
            has_cross_sale_expandable_list: false,
            is_saleable: product?.availability?.isInStock || false,
            has_cross_sale_products: crossSells.length > 0,
            has_bundled_expandable_list: bundledProducts.length > 3,
            has_bundled_products: bundledProducts.length > 0,
            has_linked_products: linkedProducts.length > 0,
            has_reviews: (product?.productReviews?.productReviews || []).length > 0,
            has_sizes: availableVariants.length > 0,
            is_on_sale: (product?.masterVariant?.price?.discount?.value || 0) > 0,
            has_video: (firstVideoIndex || -1) > -1,
          },
          pageName: 'product',
        },
        page,
      ),
      item: page as Page,
    },
  }
}

const Product = PageHOC<ProductProps>(({ item }) => {
  const components = useBlockComponents(item.blocks || [])
  const product = getProductFieldValue(item.fields || [], 'product')

  useEffect(() => {
    if (typeof window !== 'undefined' && product?.id) {
      trackProductClick(product.id)
      gtm({
        // TODO: These categoryIds should be consolidated in GTM. But current GTM variable setup is using both.
        categoryContentIds: join(product.categoryIDs, ','),
        categoryIds: join(product.categoryIDs, ','),
        productData: {
          id: product.id,
          // @ts-ignore
          localPrice: toNumber(product.masterVariant?.price?.discount?.plain) || toNumber(product.masterVariant?.price?.plain) || 0,
        },
        productName: product.name || '',
        productId: parseInt(product.id, 10),
      })
    }
  }, [product])

  return (
    <ProductContext.Provider value={product || undefined}>
      <OneColumn footer={<FooterWithGroupVisibility />} header={<HeaderWithGroupVisibility />}>
        <SinglePostWrap>
          {components}
        </SinglePostWrap>
      </OneColumn>
      <BackToTop />
      <ProductLinkedData />
    </ProductContext.Provider>
  )
})

export default Product
